/**
 * Top 500 US cities with coordinates for distance calculations
 * Format: [city, stateCode, state, population, latitude, longitude]
 * 
 * This is the unified source of truth for city data, including coordinates.
 * Used by both LocationAutocomplete and distance filtering.
 */

export interface USCityWithCoords {
  city: string;
  stateCode: string;
  state: string;
  population: number;
  lat: number;
  lng: number;
}

// Top 500 US cities by population with coordinates
const citiesWithCoordsData: [string, string, string, number, number, number][] = [
  ['New York', 'NY', 'New York', 8336817, 40.7128, -74.0060],
  ['Los Angeles', 'CA', 'California', 3979576, 34.0522, -118.2437],
  ['Chicago', 'IL', 'Illinois', 2693976, 41.8781, -87.6298],
  ['Houston', 'TX', 'Texas', 2320268, 29.7604, -95.3698],
  ['Phoenix', 'AZ', 'Arizona', 1680992, 33.4484, -112.0740],
  ['Philadelphia', 'PA', 'Pennsylvania', 1584064, 39.9526, -75.1652],
  ['San Antonio', 'TX', 'Texas', 1547253, 29.4241, -98.4936],
  ['San Diego', 'CA', 'California', 1423851, 32.7157, -117.1611],
  ['Dallas', 'TX', 'Texas', 1343573, 32.7767, -96.7970],
  ['San Jose', 'CA', 'California', 1021795, 37.3382, -121.8863],
  ['Austin', 'TX', 'Texas', 978908, 30.2672, -97.7431],
  ['Jacksonville', 'FL', 'Florida', 911507, 30.3322, -81.6557],
  ['Fort Worth', 'TX', 'Texas', 909585, 32.7555, -97.3308],
  ['Columbus', 'OH', 'Ohio', 898553, 39.9612, -82.9988],
  ['Indianapolis', 'IN', 'Indiana', 876384, 39.7684, -86.1581],
  ['Charlotte', 'NC', 'North Carolina', 872498, 35.2271, -80.8431],
  ['San Francisco', 'CA', 'California', 873965, 37.7749, -122.4194],
  ['Seattle', 'WA', 'Washington', 737015, 47.6062, -122.3321],
  ['Denver', 'CO', 'Colorado', 727211, 39.7392, -104.9903],
  ['Washington', 'DC', 'District of Columbia', 689545, 38.9072, -77.0369],
  ['Nashville', 'TN', 'Tennessee', 689447, 36.1627, -86.7816],
  ['Oklahoma City', 'OK', 'Oklahoma', 681054, 35.4676, -97.5164],
  ['El Paso', 'TX', 'Texas', 678815, 31.7619, -106.4850],
  ['Boston', 'MA', 'Massachusetts', 675647, 42.3601, -71.0589],
  ['Portland', 'OR', 'Oregon', 654741, 45.5152, -122.6784],
  ['Las Vegas', 'NV', 'Nevada', 641903, 36.1699, -115.1398],
  ['Detroit', 'MI', 'Michigan', 639111, 42.3314, -83.0458],
  ['Memphis', 'TN', 'Tennessee', 633104, 35.1495, -90.0490],
  ['Louisville', 'KY', 'Kentucky', 617638, 38.2527, -85.7585],
  ['Baltimore', 'MD', 'Maryland', 585708, 39.2904, -76.6122],
  ['Milwaukee', 'WI', 'Wisconsin', 577222, 43.0389, -87.9065],
  ['Albuquerque', 'NM', 'New Mexico', 564559, 35.0844, -106.6504],
  ['Tucson', 'AZ', 'Arizona', 548073, 32.2226, -110.9747],
  ['Fresno', 'CA', 'California', 542107, 36.7378, -119.7871],
  ['Sacramento', 'CA', 'California', 524943, 38.5816, -121.4944],
  ['Kansas City', 'MO', 'Missouri', 508090, 39.0997, -94.5786],
  ['Mesa', 'AZ', 'Arizona', 504258, 33.4152, -111.8315],
  ['Atlanta', 'GA', 'Georgia', 498715, 33.7490, -84.3880],
  ['Omaha', 'NE', 'Nebraska', 486051, 41.2565, -95.9345],
  ['Colorado Springs', 'CO', 'Colorado', 478961, 38.8339, -104.8214],
  ['Raleigh', 'NC', 'North Carolina', 474069, 35.7796, -78.6382],
  ['Long Beach', 'CA', 'California', 466742, 33.7701, -118.1937],
  ['Virginia Beach', 'VA', 'Virginia', 459470, 36.8529, -75.9780],
  ['Miami', 'FL', 'Florida', 454279, 25.7617, -80.1918],
  ['Oakland', 'CA', 'California', 433031, 37.8044, -122.2712],
  ['Minneapolis', 'MN', 'Minnesota', 429954, 44.9778, -93.2650],
  ['Tulsa', 'OK', 'Oklahoma', 413066, 36.1540, -95.9928],
  ['Bakersfield', 'CA', 'California', 403455, 35.3733, -119.0187],
  ['Wichita', 'KS', 'Kansas', 397532, 37.6872, -97.3301],
  ['Arlington', 'TX', 'Texas', 394266, 32.7357, -97.1081],
  ['Aurora', 'CO', 'Colorado', 386261, 39.7294, -104.8319],
  ['Tampa', 'FL', 'Florida', 399700, 27.9506, -82.4572],
  ['New Orleans', 'LA', 'Louisiana', 383997, 29.9511, -90.0715],
  ['Cleveland', 'OH', 'Ohio', 372624, 41.4993, -81.6944],
  ['Honolulu', 'HI', 'Hawaii', 350964, 21.3069, -157.8583],
  ['Anaheim', 'CA', 'California', 350365, 33.8366, -117.9143],
  ['Lexington', 'KY', 'Kentucky', 322570, 38.0406, -84.5037],
  ['Stockton', 'CA', 'California', 320804, 37.9577, -121.2908],
  ['Corpus Christi', 'TX', 'Texas', 317863, 27.8006, -97.3964],
  ['Henderson', 'NV', 'Nevada', 317610, 36.0395, -114.9817],
  ['Riverside', 'CA', 'California', 314998, 33.9806, -117.3755],
  ['Newark', 'NJ', 'New Jersey', 311549, 40.7357, -74.1724],
  ['Saint Paul', 'MN', 'Minnesota', 308096, 44.9537, -93.0900],
  ['Santa Ana', 'CA', 'California', 310227, 33.7455, -117.8677],
  ['Cincinnati', 'OH', 'Ohio', 309317, 39.1031, -84.5120],
  ['Irvine', 'CA', 'California', 307670, 33.6846, -117.8265],
  ['Orlando', 'FL', 'Florida', 307573, 28.5383, -81.3792],
  ['Pittsburgh', 'PA', 'Pennsylvania', 302971, 40.4406, -79.9959],
  ['St. Louis', 'MO', 'Missouri', 301578, 38.6270, -90.1994],
  ['Greensboro', 'NC', 'North Carolina', 299035, 36.0726, -79.7920],
  ['Jersey City', 'NJ', 'New Jersey', 292449, 40.7178, -74.0431],
  ['Anchorage', 'AK', 'Alaska', 291247, 61.2181, -149.9003],
  ['Lincoln', 'NE', 'Nebraska', 289102, 40.8258, -96.6852],
  ['Plano', 'TX', 'Texas', 287677, 33.0198, -96.6989],
  ['Durham', 'NC', 'North Carolina', 283506, 35.9940, -78.8986],
  ['Buffalo', 'NY', 'New York', 278349, 42.8864, -78.8784],
  ['Chandler', 'AZ', 'Arizona', 275987, 33.3062, -111.8413],
  ['Chula Vista', 'CA', 'California', 275487, 32.6401, -117.0842],
  ['Toledo', 'OH', 'Ohio', 270871, 41.6528, -83.5379],
  ['Madison', 'WI', 'Wisconsin', 269840, 43.0731, -89.4012],
  ['Gilbert', 'AZ', 'Arizona', 267918, 33.3528, -111.7890],
  ['Reno', 'NV', 'Nevada', 264165, 39.5296, -119.8138],
  ['Fort Wayne', 'IN', 'Indiana', 263886, 41.0793, -85.1394],
  ['North Las Vegas', 'NV', 'Nevada', 262527, 36.1989, -115.1175],
  ['St. Petersburg', 'FL', 'Florida', 258308, 27.7676, -82.6403],
  ['Lubbock', 'TX', 'Texas', 258862, 33.5779, -101.8552],
  ['Irving', 'TX', 'Texas', 256684, 32.8140, -96.9489],
  ['Laredo', 'TX', 'Texas', 255205, 27.5306, -99.4803],
  ['Winston-Salem', 'NC', 'North Carolina', 249545, 36.0999, -80.2442],
  ['Chesapeake', 'VA', 'Virginia', 249422, 36.7682, -76.2875],
  ['Glendale', 'AZ', 'Arizona', 248325, 33.5387, -112.1860],
  ['Garland', 'TX', 'Texas', 246018, 32.9126, -96.6389],
  ['Scottsdale', 'AZ', 'Arizona', 241361, 33.4942, -111.9261],
  ['Norfolk', 'VA', 'Virginia', 238005, 36.8508, -76.2859],
  ['Boise', 'ID', 'Idaho', 235684, 43.6150, -116.2023],
  ['Fremont', 'CA', 'California', 230504, 37.5485, -121.9886],
  ['Spokane', 'WA', 'Washington', 228989, 47.6588, -117.4260],
  ['Santa Clarita', 'CA', 'California', 228673, 34.3917, -118.5426],
  ['Richmond', 'VA', 'Virginia', 226610, 37.5407, -77.4360],
  ['San Bernardino', 'CA', 'California', 222101, 34.1083, -117.2898],
  ['Baton Rouge', 'LA', 'Louisiana', 220236, 30.4583, -91.1403],
  ['Hialeah', 'FL', 'Florida', 219917, 25.8576, -80.2781],
  ['Tacoma', 'WA', 'Washington', 219346, 47.2529, -122.4443],
  ['Modesto', 'CA', 'California', 218464, 37.6391, -120.9969],
  ['Port St. Lucie', 'FL', 'Florida', 217863, 27.2730, -80.3582],
  ['Huntsville', 'AL', 'Alabama', 215006, 34.7304, -86.5861],
  ['Des Moines', 'IA', 'Iowa', 214237, 41.5868, -93.6250],
  ['Moreno Valley', 'CA', 'California', 212751, 33.9425, -117.2297],
  ['Fontana', 'CA', 'California', 214547, 34.0922, -117.4350],
  ['Frisco', 'TX', 'Texas', 200509, 33.1507, -96.8236],
  ['Rochester', 'NY', 'New York', 205695, 43.1566, -77.6088],
  ['Yonkers', 'NY', 'New York', 211569, 40.9312, -73.8987],
  ['Fayetteville', 'NC', 'North Carolina', 211657, 35.0527, -78.8784],
  ['Worcester', 'MA', 'Massachusetts', 206518, 42.2626, -71.8023],
  ['Columbus', 'GA', 'Georgia', 206922, 32.4610, -84.9877],
  ['Cape Coral', 'FL', 'Florida', 204510, 26.5629, -81.9495],
  ['McKinney', 'TX', 'Texas', 199177, 33.1972, -96.6397],
  ['Little Rock', 'AR', 'Arkansas', 202591, 34.7465, -92.2896],
  ['Oxnard', 'CA', 'California', 202063, 34.1975, -119.1771],
  ['Amarillo', 'TX', 'Texas', 199924, 35.2220, -101.8313],
  ['Augusta', 'GA', 'Georgia', 202081, 33.4735, -82.0105],
  ['Salt Lake City', 'UT', 'Utah', 199723, 40.7608, -111.8910],
  ['Montgomery', 'AL', 'Alabama', 198218, 32.3792, -86.3077],
  ['Birmingham', 'AL', 'Alabama', 197575, 33.5207, -86.8025],
  ['Grand Rapids', 'MI', 'Michigan', 198917, 42.9634, -85.6681],
  ['Grand Prairie', 'TX', 'Texas', 196100, 32.7459, -97.0086],
  ['Overland Park', 'KS', 'Kansas', 197238, 38.9822, -94.6708],
  ['Tallahassee', 'FL', 'Florida', 196169, 30.4383, -84.2807],
  ['Huntington Beach', 'CA', 'California', 198711, 33.6603, -117.9992],
  ['Sioux Falls', 'SD', 'South Dakota', 192517, 43.5460, -96.7313],
  ['Peoria', 'AZ', 'Arizona', 190985, 33.5806, -112.2374],
  ['Knoxville', 'TN', 'Tennessee', 190740, 35.9606, -83.9207],
  ['Glendale', 'CA', 'California', 196543, 34.1425, -118.2551],
  ['Vancouver', 'WA', 'Washington', 190915, 45.6387, -122.6615],
  ['Providence', 'RI', 'Rhode Island', 190934, 41.8240, -71.4128],
  ['Akron', 'OH', 'Ohio', 190469, 41.0814, -81.5190],
  ['Brownsville', 'TX', 'Texas', 186738, 25.9017, -97.4975],
  ['Mobile', 'AL', 'Alabama', 187041, 30.6954, -88.0399],
  ['Newport News', 'VA', 'Virginia', 186247, 37.0871, -76.4730],
  ['Tempe', 'AZ', 'Arizona', 185038, 33.4255, -111.9400],
  ['Shreveport', 'LA', 'Louisiana', 187593, 32.5252, -93.7502],
  ['Chattanooga', 'TN', 'Tennessee', 181099, 35.0456, -85.3097],
  ['Fort Lauderdale', 'FL', 'Florida', 182760, 26.1224, -80.1373],
  ['Aurora', 'IL', 'Illinois', 180542, 41.7606, -88.3201],
  ['Elk Grove', 'CA', 'California', 176124, 38.4088, -121.3716],
  ['Ontario', 'CA', 'California', 175265, 34.0633, -117.6509],
  ['Salem', 'OR', 'Oregon', 175535, 44.9429, -123.0351],
  ['Cary', 'NC', 'North Carolina', 174721, 35.7915, -78.7811],
  ['Santa Rosa', 'CA', 'California', 178127, 38.4405, -122.7144],
  ['Rancho Cucamonga', 'CA', 'California', 177603, 34.1064, -117.5931],
  ['Eugene', 'OR', 'Oregon', 176654, 44.0521, -123.0868],
  ['Oceanside', 'CA', 'California', 176193, 33.1959, -117.3795],
  ['Clarksville', 'TN', 'Tennessee', 166722, 36.5298, -87.3595],
  ['Garden Grove', 'CA', 'California', 172646, 33.7739, -117.9414],
  ['Lancaster', 'CA', 'California', 173516, 34.6868, -118.1542],
  ['Springfield', 'MO', 'Missouri', 169176, 37.2090, -93.2923],
  ['Pembroke Pines', 'FL', 'Florida', 171178, 26.0128, -80.2239],
  ['Fort Collins', 'CO', 'Colorado', 169810, 40.5853, -105.0844],
  ['Palmdale', 'CA', 'California', 169450, 34.5794, -118.1165],
  ['Salinas', 'CA', 'California', 163542, 36.6777, -121.6555],
  ['Hayward', 'CA', 'California', 162954, 37.6688, -122.0808],
  ['Corona', 'CA', 'California', 157136, 33.8753, -117.5664],
  ['Paterson', 'NJ', 'New Jersey', 159732, 40.9168, -74.1718],
  ['Murfreesboro', 'TN', 'Tennessee', 152769, 35.8456, -86.3903],
  ['Macon', 'GA', 'Georgia', 157346, 32.8407, -83.6324],
  ['Lakewood', 'CO', 'Colorado', 155984, 39.7047, -105.0814],
  ['Killeen', 'TX', 'Texas', 153095, 31.1171, -97.7278],
  ['Springfield', 'MA', 'Massachusetts', 155929, 42.1015, -72.5898],
  ['Alexandria', 'VA', 'Virginia', 159428, 38.8048, -77.0469],
  ['Kansas City', 'KS', 'Kansas', 156607, 39.1155, -94.6268],
  ['Sunnyvale', 'CA', 'California', 155805, 37.3688, -122.0363],
  ['Hollywood', 'FL', 'Florida', 154823, 26.0112, -80.1495],
  ['Roseville', 'CA', 'California', 147773, 38.7521, -121.2880],
  ['Charleston', 'SC', 'South Carolina', 150227, 32.7765, -79.9311],
  ['Escondido', 'CA', 'California', 151038, 33.1192, -117.0864],
  ['Joliet', 'IL', 'Illinois', 150362, 41.5250, -88.0817],
  ['Jackson', 'MS', 'Mississippi', 153701, 32.2988, -90.1848],
  ['Bellevue', 'WA', 'Washington', 151854, 47.6101, -122.2015],
  ['Surprise', 'AZ', 'Arizona', 143148, 33.6292, -112.3680],
  ['Naperville', 'IL', 'Illinois', 149540, 41.7508, -88.1535],
  ['Pasadena', 'TX', 'Texas', 151227, 29.6911, -95.2091],
  ['Pomona', 'CA', 'California', 151348, 34.0551, -117.7500],
  ['Bridgeport', 'CT', 'Connecticut', 148654, 41.1865, -73.1952],
  ['Denton', 'TX', 'Texas', 139869, 33.2148, -97.1331],
  ['Rockford', 'IL', 'Illinois', 148655, 42.2711, -89.0940],
  ['Mesquite', 'TX', 'Texas', 150108, 32.7668, -96.5992],
  ['Savannah', 'GA', 'Georgia', 147780, 32.0809, -81.0912],
  ['Syracuse', 'NY', 'New York', 148620, 43.0481, -76.1474],
  ['McAllen', 'TX', 'Texas', 142212, 26.2034, -98.2300],
  ['Torrance', 'CA', 'California', 143592, 33.8358, -118.3406],
  ['Olathe', 'KS', 'Kansas', 141290, 38.8814, -94.8191],
  ['Visalia', 'CA', 'California', 141384, 36.3302, -119.2921],
  ['Thornton', 'CO', 'Colorado', 141867, 39.8680, -104.9720],
  ['Fullerton', 'CA', 'California', 139132, 33.8704, -117.9242],
  ['Gainesville', 'FL', 'Florida', 141085, 29.6516, -82.3248],
  ['Waco', 'TX', 'Texas', 138486, 31.5493, -97.1467],
  ['West Valley City', 'UT', 'Utah', 140230, 40.6916, -112.0011],
  ['Warren', 'MI', 'Michigan', 139387, 42.5145, -83.0147],
  ['Lakewood', 'NJ', 'New Jersey', 135158, 40.0979, -74.2177],
  ['Hampton', 'VA', 'Virginia', 134510, 37.0299, -76.3452],
  ['Dayton', 'OH', 'Ohio', 137644, 39.7589, -84.1916],
  ['Columbia', 'SC', 'South Carolina', 136632, 34.0007, -81.0348],
  ['Orange', 'CA', 'California', 139911, 33.7879, -117.8531],
  ['Cedar Rapids', 'IA', 'Iowa', 137710, 41.9779, -91.6656],
  ['Stamford', 'CT', 'Connecticut', 135470, 41.0534, -73.5387],
  ['Victorville', 'CA', 'California', 134810, 34.5362, -117.2928],
  ['Pasadena', 'CA', 'California', 138699, 34.1478, -118.1445],
  ['Elizabeth', 'NJ', 'New Jersey', 137298, 40.6640, -74.2107],
  ['New Haven', 'CT', 'Connecticut', 134023, 41.3082, -72.9282],
  ['Miramar', 'FL', 'Florida', 134721, 25.9860, -80.2323],
  ['Kent', 'WA', 'Washington', 136588, 47.3809, -122.2348],
  ['Sterling Heights', 'MI', 'Michigan', 134346, 42.5803, -83.0302],
  ['Carrollton', 'TX', 'Texas', 133168, 32.9537, -96.8903],
  ['Coral Springs', 'FL', 'Florida', 134394, 26.2712, -80.2706],
  ['Midland', 'TX', 'Texas', 132950, 31.9973, -102.0779],
  ['Norman', 'OK', 'Oklahoma', 128026, 35.2226, -97.4395],
  ['Athens', 'GA', 'Georgia', 127315, 33.9519, -83.3576],
  ['Santa Clara', 'CA', 'California', 127647, 37.3541, -121.9552],
  ['Columbia', 'MO', 'Missouri', 126254, 38.9517, -92.3341],
  ['Fargo', 'ND', 'North Dakota', 125990, 46.8772, -96.7898],
  ['Pearland', 'TX', 'Texas', 125828, 29.5636, -95.2860],
  ['Simi Valley', 'CA', 'California', 126356, 34.2694, -118.7815],
  ['Topeka', 'KS', 'Kansas', 126587, 39.0473, -95.6752],
  ['Meridian', 'ID', 'Idaho', 117635, 43.6121, -116.3915],
  ['Allentown', 'PA', 'Pennsylvania', 126092, 40.6084, -75.4902],
  ['Thousand Oaks', 'CA', 'California', 126966, 34.1706, -118.8376],
  ['Abilene', 'TX', 'Texas', 125182, 32.4487, -99.7331],
  ['Vallejo', 'CA', 'California', 121253, 38.1041, -122.2566],
  ['Concord', 'CA', 'California', 129295, 37.9780, -122.0311],
  ['Round Rock', 'TX', 'Texas', 128739, 30.5083, -97.6789],
  ['Arvada', 'CO', 'Colorado', 124402, 39.8028, -105.0875],
  ['Clovis', 'CA', 'California', 120124, 36.8252, -119.7029],
  ['Palm Bay', 'FL', 'Florida', 119760, 28.0345, -80.5887],
  ['Independence', 'MO', 'Missouri', 123011, 39.0911, -94.4155],
  ['Lafayette', 'LA', 'Louisiana', 121374, 30.2241, -92.0198],
  ['Ann Arbor', 'MI', 'Michigan', 123851, 42.2808, -83.7430],
  ['Rochester', 'MN', 'Minnesota', 121395, 44.0121, -92.4802],
  ['Hartford', 'CT', 'Connecticut', 121054, 41.7658, -72.6734],
  ['College Station', 'TX', 'Texas', 119304, 30.6280, -96.3344],
  ['Wilmington', 'NC', 'North Carolina', 115933, 34.2257, -77.9447],
  ['North Charleston', 'SC', 'South Carolina', 114852, 32.8546, -79.9748],
  ['Billings', 'MT', 'Montana', 117116, 45.7833, -108.5007],
  ['West Palm Beach', 'FL', 'Florida', 117415, 26.7153, -80.0534],
  ['Berkeley', 'CA', 'California', 124321, 37.8716, -122.2727],
  ['Cambridge', 'MA', 'Massachusetts', 118403, 42.3736, -71.1097],
  ['Clearwater', 'FL', 'Florida', 117295, 27.9659, -82.8001],
  ['West Jordan', 'UT', 'Utah', 116961, 40.6097, -111.9391],
  ['Evansville', 'IN', 'Indiana', 117298, 37.9716, -87.5711],
  ['Richardson', 'TX', 'Texas', 116783, 32.9483, -96.7299],
  ['Broken Arrow', 'OK', 'Oklahoma', 113540, 36.0609, -95.7975],
  ['League City', 'TX', 'Texas', 115595, 29.5075, -95.0949],
  ['Manchester', 'NH', 'New Hampshire', 115644, 42.9956, -71.4548],
  ['Lakeland', 'FL', 'Florida', 112641, 28.0395, -81.9498],
  ['Carlsbad', 'CA', 'California', 114746, 33.1581, -117.3506],
  ['Antioch', 'CA', 'California', 115291, 38.0049, -121.8058],
  ['Westminster', 'CO', 'Colorado', 116317, 39.8367, -105.0372],
  ['High Point', 'NC', 'North Carolina', 114059, 35.9557, -80.0053],
  ['Provo', 'UT', 'Utah', 115162, 40.2338, -111.6585],
  ['Lowell', 'MA', 'Massachusetts', 115554, 42.6334, -71.3162],
  ['Elgin', 'IL', 'Illinois', 112456, 42.0354, -88.2826],
  ['Waterbury', 'CT', 'Connecticut', 114403, 41.5582, -73.0515],
  ['Springfield', 'IL', 'Illinois', 114394, 39.7817, -89.6501],
  ['Gresham', 'OR', 'Oregon', 114247, 45.5001, -122.4302],
  ['Murrieta', 'CA', 'California', 113479, 33.5539, -117.2139],
  ['Lewisville', 'TX', 'Texas', 111822, 33.0462, -96.9942],
  ['Las Cruces', 'NM', 'New Mexico', 111385, 32.3199, -106.7637],
  ['Lansing', 'MI', 'Michigan', 112644, 42.7325, -84.5555],
  ['Beaumont', 'TX', 'Texas', 115282, 30.0802, -94.1266],
  ['Odessa', 'TX', 'Texas', 114441, 31.8457, -102.3676],
  ['Pueblo', 'CO', 'Colorado', 111876, 38.2544, -104.6091],
  ['Peoria', 'IL', 'Illinois', 113150, 40.6936, -89.5890],
  ['Downey', 'CA', 'California', 111126, 33.9401, -118.1332],
  ['Pompano Beach', 'FL', 'Florida', 112046, 26.2379, -80.1248],
  ['Miami Gardens', 'FL', 'Florida', 111640, 25.9420, -80.2456],
  ['Temecula', 'CA', 'California', 110003, 33.4936, -117.1484],
  ['Everett', 'WA', 'Washington', 110629, 47.9790, -122.2021],
  ['Costa Mesa', 'CA', 'California', 112174, 33.6412, -117.9187],
  ['San Buenaventura', 'CA', 'California', 109106, 34.2746, -119.2290],
  ['Sparks', 'NV', 'Nevada', 108445, 39.5349, -119.7527],
  ['Santa Maria', 'CA', 'California', 109576, 34.9530, -120.4357],
  ['Sugar Land', 'TX', 'Texas', 111026, 29.6197, -95.6349],
  ['Greeley', 'CO', 'Colorado', 108795, 40.4233, -104.7091],
  ['South Fulton', 'GA', 'Georgia', 107436, 33.6268, -84.5802],
  ['Dearborn', 'MI', 'Michigan', 108420, 42.3223, -83.1763],
  ['Concord', 'NC', 'North Carolina', 105240, 35.4088, -80.5795],
  ['Tyler', 'TX', 'Texas', 107405, 32.3513, -95.3011],
  ['Sandy Springs', 'GA', 'Georgia', 108080, 33.9304, -84.3733],
  ['West Covina', 'CA', 'California', 106098, 34.0686, -117.9390],
  ['Green Bay', 'WI', 'Wisconsin', 107395, 44.5133, -88.0133],
  ['Centennial', 'CO', 'Colorado', 108418, 39.5807, -104.8772],
  ['Jurupa Valley', 'CA', 'California', 111864, 33.9972, -117.4855],
  ['El Monte', 'CA', 'California', 113475, 34.0686, -118.0276],
  ['Allen', 'TX', 'Texas', 104627, 33.1032, -96.6706],
  ['Hillsboro', 'OR', 'Oregon', 106894, 45.5229, -122.9898],
  ['Menifee', 'CA', 'California', 102527, 33.6781, -117.1460],
  ['Nampa', 'ID', 'Idaho', 100200, 43.5407, -116.5635],
  ['Spokane Valley', 'WA', 'Washington', 102976, 47.6732, -117.2394],
  ['Rio Rancho', 'NM', 'New Mexico', 104046, 35.2328, -106.6644],
  ['Brockton', 'MA', 'Massachusetts', 105643, 42.0834, -71.0184],
  ['El Cajon', 'CA', 'California', 106215, 32.7948, -116.9625],
  ['Burbank', 'CA', 'California', 107337, 34.1808, -118.3090],
  ['Inglewood', 'CA', 'California', 107762, 33.9617, -118.3531],
  ['Renton', 'WA', 'Washington', 106785, 47.4829, -122.2171],
  ['Davie', 'FL', 'Florida', 105691, 26.0765, -80.2521],
  ['South Bend', 'IN', 'Indiana', 103453, 41.6764, -86.2520],
  ['Rialto', 'CA', 'California', 104026, 34.1064, -117.3703],
  ['Boulder', 'CO', 'Colorado', 108250, 40.0150, -105.2705],
  ['San Mateo', 'CA', 'California', 105661, 37.5630, -122.3255],
  ['Edison', 'NJ', 'New Jersey', 107588, 40.5187, -74.4121],
  ['Vacaville', 'CA', 'California', 103078, 38.3566, -121.9877],
  ['Woodbridge', 'NJ', 'New Jersey', 103639, 40.5574, -74.2846],
  ['Bend', 'OR', 'Oregon', 99178, 44.0582, -121.3153],
  ['Goodyear', 'AZ', 'Arizona', 95294, 33.4353, -112.3587],
  ['Wichita Falls', 'TX', 'Texas', 104553, 33.9137, -98.4934],
  ['San Angelo', 'TX', 'Texas', 101004, 31.4638, -100.4370],
  ["Lee's Summit", 'MO', 'Missouri', 101108, 38.9108, -94.3822],
  ['Fairfield', 'CA', 'California', 119881, 38.2494, -122.0400],
  ['Davenport', 'IA', 'Iowa', 101724, 41.5236, -90.5776],
  ['Edinburg', 'TX', 'Texas', 101170, 26.3017, -98.1633],
  ['South Gate', 'CA', 'California', 95677, 33.9547, -118.2120],
  ['Buckeye', 'AZ', 'Arizona', 91502, 33.3703, -112.5838],
  ['Fishers', 'IN', 'Indiana', 98977, 39.9568, -86.0186],
  ['Norwalk', 'CA', 'California', 105549, 33.9022, -118.0817],
  ['Westminster', 'CA', 'California', 91137, 33.7514, -117.9940],
  ['Flint', 'MI', 'Michigan', 95943, 43.0125, -83.6875],
  ['Livermore', 'CA', 'California', 90189, 37.6819, -121.7680],
  ['Chico', 'CA', 'California', 101475, 39.7285, -121.8375],
  ['Sunrise', 'FL', 'Florida', 97355, 26.1669, -80.2561],
  ['Orem', 'UT', 'Utah', 97499, 40.2969, -111.6946],
  ['New Braunfels', 'TX', 'Texas', 90209, 29.7030, -98.1245],
  ['San Leandro', 'CA', 'California', 91489, 37.7249, -122.1561],
  ['Reading', 'PA', 'Pennsylvania', 95112, 40.3356, -75.9269],
  ['Clinton', 'MI', 'Michigan', 102475, 42.5867, -82.9192],
  ['Daly City', 'CA', 'California', 104901, 37.6879, -122.4702],
  ['Newport Beach', 'CA', 'California', 85239, 33.6189, -117.9289],
  ['Boca Raton', 'FL', 'Florida', 99805, 26.3683, -80.1289],
  ['Mission Viejo', 'CA', 'California', 95290, 33.6000, -117.6720],
  ['Asheville', 'NC', 'North Carolina', 94589, 35.5951, -82.5515],
  ['Quincy', 'MA', 'Massachusetts', 101636, 42.2529, -71.0023],
  ['Lynn', 'MA', 'Massachusetts', 101253, 42.4668, -70.9495],
  ['Redding', 'CA', 'California', 92642, 40.5865, -122.3917],
  ['San Marcos', 'TX', 'Texas', 67553, 29.8833, -97.9414],
  ['Carmel', 'IN', 'Indiana', 99757, 39.9784, -86.1180],
  ['Yuma', 'AZ', 'Arizona', 95548, 32.6927, -114.6277],
  ['Albany', 'NY', 'New York', 99224, 42.6526, -73.7562],
  ['Plantation', 'FL', 'Florida', 94580, 26.1276, -80.2331],
  ['Napa', 'CA', 'California', 79068, 38.2975, -122.2869],
  ['Tuscaloosa', 'AL', 'Alabama', 99600, 33.2098, -87.5692],
  ['Santa Cruz', 'CA', 'California', 64608, 36.9741, -122.0308],
  ['Hesperia', 'CA', 'California', 99818, 34.4264, -117.3009],
  ['Compton', 'CA', 'California', 97877, 33.8958, -118.2201],
  ['Carson', 'CA', 'California', 95617, 33.8317, -118.2820],
  ['Trenton', 'NJ', 'New Jersey', 90457, 40.2170, -74.7429],
  ['Citrus Heights', 'CA', 'California', 87796, 38.7071, -121.2810],
  ['Lake Forest', 'CA', 'California', 85858, 33.6469, -117.6891],
  ['Lawrence', 'KS', 'Kansas', 94934, 38.9717, -95.2353],
  ['Federal Way', 'WA', 'Washington', 101030, 47.3223, -122.3126],
  ['Santa Monica', 'CA', 'California', 93076, 34.0195, -118.4912],
  ['Lake Charles', 'LA', 'Louisiana', 84032, 30.2266, -93.2174],
  ['Livonia', 'MI', 'Michigan', 93657, 42.3684, -83.3527],
  ['Tracy', 'CA', 'California', 93000, 37.7397, -121.4252],
  ['Fort Smith', 'AR', 'Arkansas', 89142, 35.3859, -94.3985],
  ['Bloomington', 'IL', 'Illinois', 78025, 40.4842, -88.9937],
  ['Sioux City', 'IA', 'Iowa', 85797, 42.4963, -96.4049],
  ['Conroe', 'TX', 'Texas', 88369, 30.3119, -95.4561],
  ['Champaign', 'IL', 'Illinois', 88302, 40.1164, -88.2434],
  ['Cicero', 'IL', 'Illinois', 81182, 41.8456, -87.7539],
  ['Farmington Hills', 'MI', 'Michigan', 83986, 42.4853, -83.3771],
  ['Longmont', 'CO', 'Colorado', 98885, 40.1672, -105.1019],
  ['Bellingham', 'WA', 'Washington', 91482, 48.7519, -122.4787],
  ['Alhambra', 'CA', 'California', 82868, 34.0953, -118.1270],
  ['Pensacola', 'FL', 'Florida', 54312, 30.4213, -87.2169],
  ['Buena Park', 'CA', 'California', 82882, 33.8675, -117.9981],
  ['Mission', 'TX', 'Texas', 85580, 26.2159, -98.3253],
  ['Suffolk', 'VA', 'Virginia', 94324, 36.7282, -76.5836],
  ['Mountain View', 'CA', 'California', 82376, 37.3861, -122.0839],
  ['Redwood City', 'CA', 'California', 84518, 37.4852, -122.2364],
  ['Indio', 'CA', 'California', 92539, 33.7206, -116.2156],
  ['Perris', 'CA', 'California', 78013, 33.7825, -117.2286],
  ['Duluth', 'MN', 'Minnesota', 93425, 46.7867, -92.1005],
  ['Chino', 'CA', 'California', 91403, 34.0122, -117.6889],
  ['San Ramon', 'CA', 'California', 84605, 37.7799, -121.9780],
  ['Lakewood', 'CA', 'California', 79794, 33.8536, -118.1340],
  ['Cranston', 'RI', 'Rhode Island', 82934, 41.7798, -71.4373],
  ['Lynchburg', 'VA', 'Virginia', 82168, 37.4138, -79.1422],
  ['Whittier', 'CA', 'California', 87306, 33.9792, -118.0328],
  ['Longview', 'TX', 'Texas', 82295, 32.5007, -94.7405],
  ['Hemet', 'CA', 'California', 90403, 33.7476, -116.9719],
  ['Pawtucket', 'RI', 'Rhode Island', 75604, 41.8787, -71.3826],
  ['Rapid City', 'SD', 'South Dakota', 74703, 44.0805, -103.2310],
  ['Hempstead', 'NY', 'New York', 55361, 40.7062, -73.6187],
  ['Warwick', 'RI', 'Rhode Island', 82823, 41.7001, -71.4162],
  ['Deerfield Beach', 'FL', 'Florida', 86859, 26.3184, -80.0998],
  ['Canton', 'OH', 'Ohio', 70447, 40.7989, -81.3784],
  ['Plymouth', 'MN', 'Minnesota', 81026, 45.0105, -93.4555],
  ['Boynton Beach', 'FL', 'Florida', 80380, 26.5254, -80.0662],
  ['Largo', 'FL', 'Florida', 84666, 27.9095, -82.7873],
  ['Delray Beach', 'FL', 'Florida', 69451, 26.4615, -80.0728],
  ['Homestead', 'FL', 'Florida', 80110, 25.4687, -80.4776],
  ['Melbourne', 'FL', 'Florida', 84678, 28.0836, -80.6081],
  ['Lauderhill', 'FL', 'Florida', 74502, 26.1403, -80.2134],
  ['Chino Hills', 'CA', 'California', 83853, 33.9898, -117.7326],
  ['Upland', 'CA', 'California', 79040, 34.0975, -117.6484],
  ['Redondo Beach', 'CA', 'California', 71686, 33.8492, -118.3884],
  ['Baldwin Park', 'CA', 'California', 77059, 34.0853, -117.9609],
  ['Miami Beach', 'FL', 'Florida', 82890, 25.7907, -80.1300],
  ['San Marcos', 'CA', 'California', 97479, 33.1434, -117.1661],
  ['Lynwood', 'CA', 'California', 72828, 33.9307, -118.2115],
  ['Yorba Linda', 'CA', 'California', 68336, 33.8886, -117.8131],
  ['Camarillo', 'CA', 'California', 70741, 34.2164, -119.0376],
  ['Sanford', 'FL', 'Florida', 61540, 28.8001, -81.2731],
  ['Walnut Creek', 'CA', 'California', 70127, 37.9101, -122.0652],
  ['Gary', 'IN', 'Indiana', 69093, 41.5934, -87.3465],
  ['Tamarac', 'FL', 'Florida', 71897, 26.2126, -80.2498],
  ['San Clemente', 'CA', 'California', 65405, 33.4269, -117.6120],
  ['Bloomington', 'MN', 'Minnesota', 89987, 44.8408, -93.2983],
  ['Manteca', 'CA', 'California', 81342, 37.7974, -121.2161],
  ['Eagan', 'MN', 'Minnesota', 68855, 44.8041, -93.1669],
  ['Rocklin', 'CA', 'California', 70757, 38.7907, -121.2358],
  ['Pleasanton', 'CA', 'California', 79871, 37.6624, -121.8747],
  ['Palo Alto', 'CA', 'California', 68572, 37.4419, -122.1430],
  ['Folsom', 'CA', 'California', 82203, 38.6780, -121.1761],
  ['Newark', 'CA', 'California', 48148, 37.5297, -122.0402],
  ['Milpitas', 'CA', 'California', 80273, 37.4323, -121.8996],
  ['San Rafael', 'CA', 'California', 62022, 37.9735, -122.5311],
  ['Cupertino', 'CA', 'California', 60170, 37.3230, -122.0322],
  ['Encinitas', 'CA', 'California', 64407, 33.0370, -117.2920],
  ['La Mesa', 'CA', 'California', 60604, 32.7678, -117.0231],
  ['Lodi', 'CA', 'California', 67582, 38.1302, -121.2724],
  ['Turlock', 'CA', 'California', 73631, 37.4947, -120.8466],
  ['Merced', 'CA', 'California', 83676, 37.3022, -120.4830],
  ['Madera', 'CA', 'California', 67006, 36.9613, -120.0607],
  ['Tulare', 'CA', 'California', 68419, 36.2077, -119.3473],
  ['Hanford', 'CA', 'California', 59104, 36.3274, -119.6457],
  ['Porterville', 'CA', 'California', 59509, 36.0652, -119.0168],
  ['Gilroy', 'CA', 'California', 59520, 37.0058, -121.5683],
  ['Morgan Hill', 'CA', 'California', 45483, 37.1305, -121.6544],
  ['Watsonville', 'CA', 'California', 52590, 36.9103, -121.7569],
  ['Seaside', 'CA', 'California', 34818, 36.6108, -121.8514],
  ['San Luis Obispo', 'CA', 'California', 47339, 35.2828, -120.6596],
  ['Paso Robles', 'CA', 'California', 31739, 35.6266, -120.6910],
  ['Pismo Beach', 'CA', 'California', 8154, 35.1428, -120.6413],
  ['Santa Barbara', 'CA', 'California', 88665, 34.4208, -119.6982],
  ['Goleta', 'CA', 'California', 32142, 34.4358, -119.8276],
  ['Lompoc', 'CA', 'California', 43509, 34.6392, -120.4579],
];

export const usCitiesWithCoords: USCityWithCoords[] = citiesWithCoordsData.map(
  ([city, stateCode, state, population, lat, lng]) => ({
    city,
    stateCode,
    state,
    population,
    lat,
    lng,
  })
);

// Create a lookup map for fast coordinate retrieval
const cityCoordinatesMap = new Map<string, { lat: number; lng: number }>();
usCitiesWithCoords.forEach((city) => {
  // Key format: "city,stateCode" in lowercase
  const key = `${city.city.toLowerCase()},${city.stateCode.toLowerCase()}`;
  cityCoordinatesMap.set(key, { lat: city.lat, lng: city.lng });
});

/**
 * Get coordinates for a city by name and state code
 * Returns null if city not found in our database
 */
export function getCityCoordinates(
  city: string,
  stateCode: string
): { lat: number; lng: number } | null {
  const key = `${city.toLowerCase()},${stateCode.toLowerCase()}`;
  return cityCoordinatesMap.get(key) || null;
}

// Pre-compute search strings for faster filtering
export const citySearchIndex = usCitiesWithCoords.map(c => ({
  ...c,
  searchString: `${c.city} ${c.state} ${c.stateCode}`.toLowerCase(),
  displayName: `${c.city}, ${c.stateCode}`
}));

/**
 * Search cities with coordinates (for autocomplete)
 */
export function searchCitiesWithCoords(
  query: string,
  limit = 10
): typeof citySearchIndex {
  if (!query || query.length < 2) return [];
  
  const lowerQuery = query.toLowerCase();
  
  return citySearchIndex
    .filter(c => c.searchString.includes(lowerQuery))
    .sort((a, b) => {
      // Prioritize exact city name starts
      const aStartsWith = a.city.toLowerCase().startsWith(lowerQuery);
      const bStartsWith = b.city.toLowerCase().startsWith(lowerQuery);
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      // Then sort by population
      return b.population - a.population;
    })
    .slice(0, limit);
}
